import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  Modal,
  ScrollView,
  StyleSheet,
  Text as RNText,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import ChessRestClient from '@/Api/ChessRestClient';

type Scope = 'trouble' | 'prominent' | 'all';
type StageStatus = 'stuck' | 'all';

type Stage = {
  key: string;
  order: number;
  label: string;
  source: string;
  broken_when: string;
  manual_checks: string[];
  counts: {
    healthy: number;
    warning: number;
    broken: number;
    users: number;
  };
};

type HealthUser = {
  user: {
    name: string;
    mobile_number: string;
  };
  order: {
    order_id: string;
    status: string;
    duration_in_months: number;
  };
  health: {
    severity: 'broken' | 'warning' | 'healthy';
    primary_stage: string;
    broken_stages: string[];
    warning_stages: string[];
    stuck_reasons: {
      stage: string;
      severity: 'broken' | 'warning' | 'healthy';
      message: string;
      evidence: Record<string, any>;
      resolution: string;
      auto_resolution?: {
        available: boolean;
        action: string;
        label: string;
        confirmation?: string;
        what_it_does?: string[];
        why_not?: string;
      };
    }[];
  };
  support: {
    latest_issue_summary: string;
    latest_request_type: string;
    open_issue_count: number;
    pending_request_count: number;
    failed_outbox_count: number;
  };
  suggested_actions: string[];
};

type DashboardPayload = {
  cached: boolean;
  summary: Record<string, number>;
  stages: Stage[];
  users: HealthUser[];
};

type ResolveResult = {
  success?: boolean;
  preview?: boolean;
  title?: string;
  content?: string;
  context_type?: string;
  requires_input?: boolean;
  requires_confirmation?: boolean;
  status?: string;
  support_request_id?: number;
  audit_id?: number;
  message?: string;
  what_it_does?: string[];
  required_fields?: string[];
  what_to_do?: string;
  plan?: AgentPlan;
  executor_response?: ResolveResult;
  proposed_rule?: {
    stage?: string;
    status?: string;
    effect?: string;
    condition?: Record<string, any>;
    agent_feedback?: string;
  };
  rollback?: {
    available: boolean;
    audit_id?: number;
    warning?: string;
  };
};

type AgentPlan = {
  confidence: number;
  issue_type: string;
  recommended_action: string;
  safe_to_auto_fix: boolean;
  requires_inputs: string[];
  backend_action?: Record<string, any>;
  why: string;
  knowledge: {
    key: string;
    title: string;
    source: string;
    content: string;
  }[];
  rollback: {
    available: boolean;
    mode: string;
    description: string;
  };
};

type AgentDryRun = {
  success: boolean;
  audit_id: number;
  plan: AgentPlan;
  case: Record<string, any>;
  snapshot: Record<string, any>;
};

type CsocAnswer = {
  success: boolean;
  answer: string;
  confidence: number;
  audit_id?: number;
  document_id?: number;
  documents?: {
    key: string;
    title: string;
    source: string;
    content: string;
    score?: number;
  }[];
  highlights?: string[];
};

type LearningContextType = 'ops_context' | 'flag_suppression';

const scopeLabels: Record<Scope, string> = {
  trouble: 'Needs attention',
  prominent: 'Important first',
  all: 'All users',
};

const Text = props => <RNText selectable {...props} />;

const stageCopy: Record<string, {label: string; plain: string; check: string}> =
  {
    payment: {
      label: 'Payment',
      plain: 'Payment is complete and the order is ready to set up.',
      check: 'Check payment/refund state before changing access.',
    },
    identity: {
      label: 'Student profile',
      plain: 'The order is linked to the right student account.',
      check: 'Check phone number, user, and player mapping.',
    },
    batch: {
      label: 'Class or plan',
      plain: 'The student has the right CSOC class, batch, or plan.',
      check: 'Check placeholder, long-order, or missing batch setup.',
    },
    registration: {
      label: 'CSOC enrollment',
      plain: 'The paid order is saved as a CSOC enrollment.',
      check: 'Check the CSOC registration row for this order.',
    },
    caissa_subscription: {
      label: 'App subscription',
      plain: 'The app subscription is active for this CSOC order.',
      check: 'Check whether subscription access was created.',
    },
    mentor: {
      label: 'Mentor',
      plain: 'The student has the correct mentor setup.',
      check: 'Check mentor name and mentor task.',
    },
    live_class: {
      label: 'Class join access',
      plain: 'The student can see and join live classes.',
      check: 'Check class visibility and Zoom meeting registration.',
    },
    resources: {
      label: 'Study material',
      plain: 'Assignments, recordings, and study material are visible.',
      check: 'Check missing assignment, recording, or course access.',
    },
    support: {
      label: 'Support status',
      plain: 'Support requests and open issues are not blocking the student.',
      check: 'Check open support requests and failed background actions.',
    },
  };

const issueCopy: Record<string, {title: string; why: string; check: string}> = {
  missing_resource_access: {
    title: 'Study material is missing',
    why: 'The student appears to be missing assignments, recordings, or course access.',
    check: 'Ask the student to refresh and confirm material is visible.',
  },
  caissa_subscription_missing: {
    title: 'App subscription is missing',
    why: 'The student is enrolled in CSOC, but app subscription access was not created.',
    check: 'Confirm the student can open paid CSOC content in the app.',
  },
  zoom_registration_missing: {
    title: 'Student is not registered for class meeting',
    why: 'The class exists, but the student is not registered for the Zoom/webinar meeting.',
    check: 'Confirm the classroom no longer shows Registration Meeting.',
  },
  pending_support_request: {
    title: 'A support request is ready to run',
    why: 'Ops already created the support request. This will run it now instead of waiting for cron.',
    check:
      'Confirm the request completed and the student-facing issue is gone.',
  },
  batch_mapping: {
    title: 'Class or batch needs review',
    why: 'The order needs the correct class, batch, or start-date decision from ops.',
    check: 'Confirm the student is placed in the intended class.',
  },
  mentor_assignment: {
    title: 'Mentor needs review',
    why: 'The mentor setup is missing or incomplete.',
    check: 'Confirm the mentor is visible and assigned correctly.',
  },
  registration_missing: {
    title: 'CSOC enrollment is missing',
    why: 'The order was paid, but the CSOC enrollment record is missing.',
    check: 'Confirm CSOC enrollment and dates are correct.',
  },
  live_class_visibility: {
    title: 'Class access needs review',
    why: 'The student may not be able to see or join the correct live class.',
    check: 'Confirm the student can see the class and join button.',
  },
  support_outbox_failure: {
    title: 'Background support action failed',
    why: 'A support automation step failed and needs manual inspection.',
    check: 'Retry or resolve the failed background action.',
  },
};

const stageLabel = (stage?: string) =>
  stageCopy[stage || '']?.label || humanizeKey(stage || 'Unknown');

const severityLabel = (severity?: string) =>
  severity === 'broken'
    ? 'Needs fix'
    : severity === 'warning'
    ? 'Needs review'
    : 'Looks ok';

const humanizeKey = (value: string) =>
  String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, letter => letter.toUpperCase());

const simplifyReasonMessage = (reason?: {stage: string; message: string}) => {
  if (!reason) {
    return 'No issue found for this filter.';
  }
  const message = (reason.message || '').toLowerCase();
  if (message.includes('zoom') || message.includes('webinar')) {
    return 'Student is not registered for the live class meeting.';
  }
  if (message.includes('cc_user_subscriptions')) {
    return 'App subscription access was not created.';
  }
  if (
    message.includes('cc_csoc_registration') ||
    message.includes('registration row')
  ) {
    return 'CSOC enrollment is missing or inactive.';
  }
  if (message.includes('mentor')) {
    return 'Mentor setup is missing or incomplete.';
  }
  if (message.includes('placeholder')) {
    return 'Placeholder class is still active after the start date.';
  }
  if (message.includes('batch')) {
    return 'Class or batch setup needs review.';
  }
  if (
    message.includes('assignment') ||
    message.includes('recording') ||
    message.includes('resource')
  ) {
    return 'Study material access may be missing.';
  }
  if (message.includes('outbox') || message.includes('failed_hard')) {
    return 'A background support action failed.';
  }
  if (message.includes('open issue') || message.includes('pending')) {
    return 'A support issue or support request still needs attention.';
  }
  return reason.message || 'This student needs review.';
};

const simplifyResolution = (text?: string) => {
  const value = String(text || '');
  const lower = value.toLowerCase();
  if (!value) {
    return 'Review this case and confirm the student can continue normally.';
  }
  if (lower.includes('order_give_missing_access')) {
    return 'Run the missing-access fix, then confirm study material is visible.';
  }
  if (lower.includes('cc_user_subscriptions')) {
    return 'Create or repair the app subscription for the correct dates.';
  }
  if (lower.includes('cc_csoc_registration')) {
    return 'Repair the CSOC enrollment after checking order details.';
  }
  if (lower.includes('zoom') || lower.includes('webinar')) {
    return 'Register the student for the upcoming class meetings.';
  }
  if (lower.includes('mentor')) {
    return 'Assign or repair the intended mentor setup.';
  }
  if (lower.includes('batch')) {
    return 'Move the student to the correct class or batch.';
  }
  if (lower.includes('outbox')) {
    return 'Fix the failed background action before closing support.';
  }
  return value;
};

const evidenceSummary = (reason?: {evidence: Record<string, any>}) => {
  const evidence = reason?.evidence || {};
  if (evidence.missing_zoom_batches?.length) {
    const first = evidence.missing_zoom_batches[0];
    if (first.missing_expected_webinar_ids?.length) {
      return `Class ${first.class_name || 'unknown'} is missing ${
        first.missing_expected_webinar_ids.length
      } master class Zoom registration(s).`;
    }
    return `Class ${first.class_name || 'unknown'} has ${
      first.webinar_count || 0
    } Zoom registration(s) missing.`;
  }
  if (evidence.subscription_count === 0) {
    return 'No active app subscription was found for this order.';
  }
  if (evidence.registration_count === 0) {
    return 'No active CSOC enrollment was found for this order.';
  }
  if (evidence.mentor_task_count === 0) {
    return 'No mentor task was found for this order.';
  }
  if (evidence.support_request_ids?.length) {
    return `Support request ${evidence.support_request_ids.join(
      ', ',
    )} needs attention.`;
  }
  if (evidence.open_issue_ids?.length || evidence.issue_ids?.length) {
    return `Open issue ${(evidence.open_issue_ids || evidence.issue_ids).join(
      ', ',
    )} is still active.`;
  }
  if (evidence.effective_start_date) {
    return `Start date ${evidence.effective_start_date}; today ${
      evidence.today || 'today'
    }.`;
  }
  return 'Technical evidence is available below.';
};

const planCopy = (plan?: AgentPlan | null, primaryReason?: any) => {
  if (!plan) {
    return {
      title: simplifyReasonMessage(primaryReason),
      why: 'Diagnosis is still loading.',
      check: 'Open this again after the diagnosis loads.',
    };
  }
  return (
    issueCopy[plan.issue_type] || {
      title: simplifyReasonMessage(primaryReason),
      why: plan.why,
      check: 'Verify the student-facing issue is resolved.',
    }
  );
};

const CSOCHealthDashboard = ({dashboardData}) => {
  const {width} = useWindowDimensions();
  const isCompact = width < 980;
  const isNarrow = width < 640;
  const userData = dashboardData?.user || {};
  const isStaff = Boolean(userData?.is_staff);
  const [scope, setScope] = useState<Scope>('trouble');
  const [stage, setStage] = useState('');
  const [stageStatus, setStageStatus] = useState<StageStatus>('stuck');
  const [search, setSearch] = useState('');
  const [issueFilter, setIssueFilter] = useState('');
  const [days, setDays] = useState('30');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState<DashboardPayload | null>(null);
  const [fixUser, setFixUser] = useState<HealthUser | null>(null);
  const [resolving, setResolving] = useState(false);
  const [agentLoading, setAgentLoading] = useState(false);
  const [contextSaving, setContextSaving] = useState(false);
  const [agentDryRun, setAgentDryRun] = useState<AgentDryRun | null>(null);
  const [resolveResult, setResolveResult] = useState<ResolveResult | null>(
    null,
  );
  const [csocQuestion, setCsocQuestion] = useState('');
  const [askingCsoc, setAskingCsoc] = useState(false);
  const [csocAnswer, setCsocAnswer] = useState<CsocAnswer | null>(null);
  const [csocAskError, setCsocAskError] = useState('');

  const summary = payload?.summary || {};

  const loadDashboard = useCallback(
    async (refresh = false) => {
      if (!isStaff) {
        return;
      }
      try {
        setLoading(true);
        setError('');
        const params = new URLSearchParams({
          scope,
          stage_status: stageStatus,
          days,
          limit: '200',
        });
        if (stage) {
          params.set('stage', stage);
        }
        if (search.trim()) {
          params.set('search', search.trim());
        }
        if (issueFilter) {
          params.set('issue', issueFilter);
        }
        if (refresh) {
          params.set('refresh', '1');
        }

        const response = await ChessRestClient.get(
          `subscription/v1/csoc/health-dashboard?${params.toString()}`,
        );
        setPayload(response.data);
      } catch (err: any) {
        setError(
          err?.response?.data?.error ||
            err?.message ||
            'Unable to load CSOC health dashboard',
        );
      } finally {
        setLoading(false);
      }
    },
    [days, isStaff, issueFilter, scope, search, stage, stageStatus],
  );

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const selectedStageLabel = useMemo(() => {
    if (!stage) {
      return 'all steps';
    }
    return stageLabel(stage);
  }, [stage]);

  const loadAgentDryRun = useCallback(async (row: HealthUser) => {
    try {
      setAgentLoading(true);
      setAgentDryRun(null);
      setResolveResult(null);
      const response = await ChessRestClient.post(
        'subscription/v1/csoc/fix-agent/dry-run',
        {
          order_id: row.order.order_id,
        },
      );
      setAgentDryRun(response.data);
    } catch (err: any) {
      setResolveResult(
        err?.response?.data || {
          success: false,
          message: err?.message || 'Unable to load agent diagnosis',
        },
      );
    } finally {
      setAgentLoading(false);
    }
  }, []);

  const askCsocKnowledge = useCallback(async () => {
    const question = csocQuestion.trim();
    if (!question) {
      setCsocAskError('Ask a CSOC question first.');
      return;
    }
    try {
      setAskingCsoc(true);
      setCsocAskError('');
      const response = await ChessRestClient.post(
        'subscription/v1/csoc/fix-agent/ask',
        {question},
      );
      setCsocAnswer(response.data);
    } catch (err: any) {
      setCsocAskError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          'Unable to ask CSOC knowledge',
      );
    } finally {
      setAskingCsoc(false);
    }
  }, [csocQuestion]);

  if (!isStaff) {
    return (
      <View style={styles.denied}>
        <Text style={styles.deniedTitle}>Staff access required</Text>
        <Text style={styles.muted}>
          This CSOC operations dashboard is only available to staff users.
        </Text>
      </View>
    );
  }

  return (
    <ScrollView
      style={[styles.page, styles.alwaysVerticalScroll]}
      showsVerticalScrollIndicator
      persistentScrollbar
      contentContainerStyle={[
        styles.content,
        isNarrow && styles.contentNarrow,
      ]}>
      <View style={[styles.topbar, isCompact && styles.topbarCompact]}>
        <View>
          <Text style={styles.title}>CSOC Student Health</Text>
          <Text style={styles.muted}>
            Start with students who need help. Open a row to see the fix.
          </Text>
        </View>
        <View style={[styles.scopeGroup, isNarrow && styles.scopeGroupNarrow]}>
          {(Object.keys(scopeLabels) as Scope[]).map(item => (
            <TouchableOpacity
              key={item}
              style={[styles.scopeButton, scope === item && styles.scopeActive]}
              onPress={() => {
                setScope(item);
                setStageStatus(item === 'all' ? 'all' : 'stuck');
              }}>
              <Text
                style={[
                  styles.scopeText,
                  scope === item && styles.scopeTextActive,
                ]}>
                {scopeLabels[item]}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      <View style={styles.toolbar}>
        <TextInput
          value={search}
          onChangeText={setSearch}
          onSubmitEditing={() => loadDashboard()}
          placeholder="Search mobile, order id, user, batch, issue id"
          style={[styles.field, styles.search, isNarrow && styles.searchNarrow]}
        />
        {['7', '30', '90', '365'].map(item => (
          <TouchableOpacity
            key={item}
            style={[styles.field, days === item && styles.filterActive]}
            onPress={() => setDays(item)}>
            <Text style={styles.filterText}>{item}d</Text>
          </TouchableOpacity>
        ))}
        <TouchableOpacity
          style={[styles.field, issueFilter && styles.filterActive]}
          onPress={() =>
            setIssueFilter(value => (value ? '' : 'placeholder_long_order'))
          }>
          <Text style={styles.filterText}>Class setup watchlist</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.field}
          onPress={() => loadDashboard(true)}>
          <Text style={styles.filterText}>Refresh</Text>
        </TouchableOpacity>
      </View>

      {error ? <Text style={styles.error}>{error}</Text> : null}

      <View style={styles.askPanel}>
        <View style={styles.askHeader}>
          <View style={styles.askTitleBlock}>
            <Text style={styles.panelTitle}>Ask the CSOC helper</Text>
            <Text style={styles.muted}>
              Ask about a student, fix, rule, or rollback.
            </Text>
          </View>
          {csocAnswer ? (
            <Text style={styles.cacheBadge}>
              {Math.round((csocAnswer.confidence || 0) * 100)}% confidence
            </Text>
          ) : null}
        </View>
        <View style={styles.askControls}>
          <TextInput
            value={csocQuestion}
            onChangeText={setCsocQuestion}
            onSubmitEditing={askCsocKnowledge}
            placeholder="Example: Why is this student unable to join class?"
            style={[styles.field, styles.askInput]}
          />
          <TouchableOpacity
            style={[
              styles.askButton,
              (!csocQuestion.trim() || askingCsoc) && styles.askButtonDisabled,
            ]}
            disabled={!csocQuestion.trim() || askingCsoc}
            onPress={askCsocKnowledge}>
            <Text style={styles.askButtonText}>
              {askingCsoc ? 'Asking...' : 'Ask'}
            </Text>
          </TouchableOpacity>
        </View>
        {csocAskError ? <Text style={styles.error}>{csocAskError}</Text> : null}
        <View style={styles.teachHint}>
          <Text style={styles.teachHintTitle}>Teach in 2 steps</Text>
          <Text style={styles.mini}>
            Open a student, write a plain note or wrong-flag reason, then review
            the preview. Nothing is saved until you confirm.
          </Text>
        </View>
        {csocAnswer?.answer ? (
          <View style={styles.answerBlock}>
            <Text style={styles.answerText}>{csocAnswer.answer}</Text>
            {csocAnswer.documents?.length ? (
              <View style={styles.answerSources}>
                <Text style={styles.cellLabel}>Sources</Text>
                {csocAnswer.documents.slice(0, 4).map(doc => (
                  <Text key={doc.key} style={styles.mini}>
                    {doc.title} - {doc.source}
                    {typeof doc.score === 'number' ? ` (${doc.score})` : ''}
                  </Text>
                ))}
              </View>
            ) : null}
          </View>
        ) : null}
      </View>

      <View style={styles.metrics}>
        <Metric
          title="Need attention"
          value={summary.trouble_users}
          tone="red"
        />
        <Metric
          title="Important first"
          value={summary.prominent_stuck_users}
          tone="red"
        />
        <Metric
          title="Placeholder class"
          value={summary.placeholder_batch_users}
          tone="red"
        />
        <Metric
          title="Long plan review"
          value={summary.long_order_batch_users}
          tone="amber"
        />
        <Metric
          title="Open issues"
          value={summary.open_user_issues}
          tone="amber"
        />
        <Metric
          title="Support pending"
          value={summary.pending_support_requests}
          tone="amber"
        />
        <Metric
          title="Background failures"
          value={summary.failed_external_actions}
          tone="red"
        />
        <Metric
          title="Healthy users"
          value={summary.healthy_users}
          tone="green"
        />
      </View>

      <View style={styles.panel}>
        <View style={styles.panelHeader}>
          <View>
            <Text style={styles.panelTitle}>Lifecycle Flow</Text>
            <Text style={styles.muted}>
              Click a step to see students blocked there. Current:{' '}
              {selectedStageLabel}
            </Text>
          </View>
          <Text style={styles.cacheBadge}>
            {loading ? 'loading' : payload?.cached ? 'cached' : 'fresh'}
          </Text>
        </View>
        <View style={styles.flow}>
          {payload?.stages?.map(stageItem => (
            <StageCard
              key={stageItem.key}
              stage={stageItem}
              selected={stage === stageItem.key}
              onPress={() => {
                setStage(stageItem.key);
                setStageStatus('stuck');
                setScope('trouble');
              }}
            />
          ))}
        </View>
      </View>

      <View style={styles.panel}>
        <View style={styles.panelHeader}>
          <View>
            <Text style={styles.panelTitle}>
              {payload?.users?.length || 0} students
            </Text>
            <Text style={styles.muted}>
              Problem, reason, and next step are shown first.
            </Text>
          </View>
        </View>
        <View style={styles.table}>
          {payload?.users?.map(row => (
            <UserRow
              key={`${row.order.order_id}-${row.user.mobile_number}`}
              row={row}
              isCompact={isCompact}
              onFixPress={() => {
                setFixUser(row);
                loadAgentDryRun(row);
              }}
            />
          ))}
        </View>
      </View>
      {fixUser ? (
        <FixModal
          row={fixUser}
          agentLoading={agentLoading}
          agentDryRun={agentDryRun}
          contextSaving={contextSaving}
          resolving={resolving}
          resolveResult={resolveResult}
          onConfirm={async () => {
            const plan = agentDryRun?.plan;
            if (!plan?.safe_to_auto_fix) {
              setResolveResult({
                success: false,
                requires_input: true,
                message: plan?.why || 'This issue needs manual input.',
                required_fields: plan?.requires_inputs || [],
                plan,
              });
              return;
            }
            try {
              setResolving(true);
              setResolveResult(null);
              const response = await ChessRestClient.post(
                'subscription/v1/csoc/fix-agent/execute',
                {
                  order_id: fixUser.order.order_id,
                  action: plan.recommended_action,
                  confirmed: true,
                },
              );
              setResolveResult(response.data);
              await loadDashboard(true);
            } catch (err: any) {
              setResolveResult(
                err?.response?.data || {
                  success: false,
                  message: err?.message || 'Unable to execute agent fix',
                },
              );
            } finally {
              setResolving(false);
            }
          }}
          onRollback={async auditId => {
            try {
              setResolving(true);
              const response = await ChessRestClient.post(
                'subscription/v1/csoc/fix-agent/rollback',
                {
                  audit_id: auditId,
                  confirmed: true,
                },
              );
              setResolveResult(response.data);
              await loadDashboard(true);
            } catch (err: any) {
              setResolveResult(
                err?.response?.data || {
                  success: false,
                  message: err?.message || 'Unable to rollback agent fix',
                },
              );
            } finally {
              setResolving(false);
            }
          }}
          onPreviewLearning={async (contextType, content) => {
            try {
              setContextSaving(true);
              const response = await ChessRestClient.post(
                'subscription/v1/csoc/fix-agent/context',
                {
                  order_id: fixUser.order.order_id,
                  title:
                    contextType === 'flag_suppression'
                      ? `Flag suppression proposal for ${fixUser.order.order_id}`
                      : `Ops context for ${fixUser.order.order_id}`,
                  content,
                  context_type: contextType,
                  stage:
                    contextType === 'flag_suppression'
                      ? fixUser.health.primary_stage
                      : undefined,
                  preview: true,
                  confirmed: false,
                },
              );
              return response.data;
            } catch (err: any) {
              const result = err?.response?.data || {
                success: false,
                message: err?.message || 'Unable to preview helper context',
              };
              setResolveResult(result);
              return result;
            } finally {
              setContextSaving(false);
            }
          }}
          onSaveLearning={async (contextType, content) => {
            try {
              setContextSaving(true);
              const response = await ChessRestClient.post(
                'subscription/v1/csoc/fix-agent/context',
                {
                  order_id: fixUser.order.order_id,
                  title:
                    contextType === 'flag_suppression'
                      ? `Flag suppression proposal for ${fixUser.order.order_id}`
                      : `Ops context for ${fixUser.order.order_id}`,
                  content,
                  context_type: contextType,
                  stage:
                    contextType === 'flag_suppression'
                      ? fixUser.health.primary_stage
                      : undefined,
                  confirmed: true,
                },
              );
              setResolveResult(response.data);
              if (contextType === 'flag_suppression') {
                await loadDashboard(true);
              }
              return response.data;
            } catch (err: any) {
              const result = err?.response?.data || {
                success: false,
                message: err?.message || 'Unable to save helper context',
              };
              setResolveResult(result);
              return result;
            } finally {
              setContextSaving(false);
            }
          }}
          onClose={() => {
            setFixUser(null);
            setAgentDryRun(null);
            setResolveResult(null);
          }}
        />
      ) : null}
    </ScrollView>
  );
};

const Metric = ({title, value = 0, tone}) => (
  <View style={[styles.metric, styles[`${tone}Border`]]}>
    <Text style={styles.metricTitle}>{title}</Text>
    <Text style={[styles.metricValue, styles[`${tone}Text`]]}>
      {value || 0}
    </Text>
  </View>
);

const StageCard = ({stage, selected, onPress}) => {
  const counts = stage.counts || {};
  const tone =
    counts.broken > 0 ? 'red' : counts.warning > 0 ? 'amber' : 'green';
  const copy = stageCopy[stage.key] || {
    label: stage.label,
    plain: stage.broken_when,
    check: (stage.manual_checks || [])[0],
  };
  return (
    <TouchableOpacity
      style={[styles.stageCard, selected && styles.stageSelected]}
      onPress={onPress}>
      <View style={[styles.stageNumber, styles[`${tone}Bg`]]}>
        <Text style={styles.stageNumberText}>{stage.order}</Text>
      </View>
      <Text style={styles.stageTitle}>{copy.label}</Text>
      <Text style={styles.stageSource}>{copy.plain}</Text>
      <View style={styles.badgeRow}>
        <Badge label={`${counts.healthy || 0} good`} tone="green" />
        <Badge label={`${counts.warning || 0} review`} tone="amber" />
        <Badge label={`${counts.broken || 0} fix`} tone="red" />
      </View>
      <Text style={styles.stageDetails} numberOfLines={4}>
        Check: {copy.check}
      </Text>
      <Text style={styles.technicalHint}>Technical: {stage.key}</Text>
    </TouchableOpacity>
  );
};

const Badge = ({label, tone}) => (
  <Text style={[styles.badge, styles[`${tone}Soft`], styles[`${tone}Text`]]}>
    {label}
  </Text>
);

const CellLabel = ({children}) => (
  <Text style={styles.cellLabel}>{children}</Text>
);

const FixModal = ({
  row,
  agentLoading,
  agentDryRun,
  contextSaving,
  resolving,
  resolveResult,
  onConfirm,
  onRollback,
  onPreviewLearning,
  onSaveLearning,
  onClose,
}: {
  row: HealthUser;
  agentLoading: boolean;
  agentDryRun: AgentDryRun | null;
  contextSaving: boolean;
  resolving: boolean;
  resolveResult: ResolveResult | null;
  onConfirm: () => void;
  onRollback: (auditId: number) => void;
  onPreviewLearning: (
    contextType: LearningContextType,
    content: string,
  ) => Promise<ResolveResult>;
  onSaveLearning: (
    contextType: LearningContextType,
    content: string,
  ) => Promise<ResolveResult>;
  onClose: () => void;
}) => {
  const [contextText, setContextText] = useState('');
  const [flagText, setFlagText] = useState('');
  const [learningDraft, setLearningDraft] = useState<{
    contextType: LearningContextType;
    content: string;
  } | null>(null);
  const [learningPreview, setLearningPreview] = useState<ResolveResult | null>(
    null,
  );
  const [learningBusy, setLearningBusy] = useState(false);
  const [learningError, setLearningError] = useState('');
  const reasons = row.health.stuck_reasons || [];
  const resolutions = reasons.length
    ? reasons.map(reason => reason.resolution).filter(Boolean)
    : row.suggested_actions || [];
  const plan = agentDryRun?.plan;
  const canAutoFix = Boolean(plan?.safe_to_auto_fix);
  const rollbackAuditId =
    resolveResult?.rollback?.audit_id || resolveResult?.audit_id;
  const primaryReason = reasons[0];
  const friendlyPlan = planCopy(plan, primaryReason);
  const autoFixSteps =
    plan?.recommended_action === 'caissa_subscription_repair'
      ? [
          'Validate paid CSOC order and active registration',
          'Create or repair app subscription access',
          'Refresh dashboard and verify subscription access',
        ]
      : plan?.recommended_action === 'zoom_registration_repair'
      ? [
          'Find upcoming live class meetings',
          'Register the order for missing sessions',
          'Verify classroom join access',
        ]
      : plan?.recommended_action === 'run_pending_support_requests'
      ? [
          'Validate pending ops-created support requests',
          'Run each mapped support-request handler immediately',
          'Refresh dashboard and verify handler side effects',
        ]
      : plan?.recommended_action === 'order_give_missing_access'
      ? [
          'Create or reuse the missing-access support request',
          'Run the missing-access handler immediately',
          'Verify resources are visible before closing support context',
        ]
      : [
          `Run ${plan?.recommended_action}`,
          'Use the existing backend support handler',
          'Refresh dashboard and verify access',
        ];
  const fixSteps = canAutoFix
    ? autoFixSteps
    : [
        ...(plan?.requires_inputs?.length
          ? [`Collect: ${plan.requires_inputs.join(', ')}`]
          : []),
        resolutions[0] || 'Review the order, registration, and support state.',
        'Apply through the existing CSOC support flow',
      ];

  const openLearningReview = async (
    contextType: LearningContextType,
    content: string,
  ) => {
    const trimmed = content.trim();
    if (!trimmed) {
      return;
    }
    setLearningDraft({contextType, content: trimmed});
    setLearningPreview(null);
    setLearningError('');
    try {
      setLearningBusy(true);
      const preview = await onPreviewLearning(contextType, trimmed);
      setLearningPreview(preview);
      if (!preview.success) {
        setLearningError(preview.message || 'Unable to preview this note.');
      }
    } finally {
      setLearningBusy(false);
    }
  };

  const confirmLearningSave = async () => {
    if (!learningDraft) {
      return;
    }
    setLearningError('');
    try {
      setLearningBusy(true);
      const result = await onSaveLearning(
        learningDraft.contextType,
        learningDraft.content,
      );
      setLearningPreview(result);
      if (result.success) {
        if (learningDraft.contextType === 'flag_suppression') {
          setFlagText('');
        } else {
          setContextText('');
        }
        setLearningDraft(null);
        setLearningPreview(null);
      } else {
        setLearningError(result.message || 'Unable to save this note.');
      }
    } finally {
      setLearningBusy(false);
    }
  };

  return (
    <Modal transparent animationType="fade" visible onRequestClose={onClose}>
      <View style={styles.modalScrim}>
        <View style={styles.fixModal}>
          <View style={styles.resolutionPanelHeader}>
            <View style={styles.modalTitleBlock}>
              <Text style={styles.resolutionPanelTitle}>
                Fix for {row.user.name || row.user.mobile_number}
              </Text>
              <Text style={styles.muted}>
                Order {row.order.order_id}. We explain first, then ask for
                approval.
              </Text>
            </View>
            <TouchableOpacity style={styles.closeButton} onPress={onClose}>
              <Text style={styles.closeButtonText}>Close</Text>
            </TouchableOpacity>
          </View>

          <ScrollView
            style={[styles.modalBody, styles.alwaysVerticalScroll]}
            showsVerticalScrollIndicator
            persistentScrollbar>
            <View style={styles.aiPlan}>
              {agentLoading ? (
                <Text style={styles.aiSummary}>Loading diagnosis...</Text>
              ) : plan ? (
                <>
                  <View style={styles.aiPlanHeader}>
                    <View style={styles.aiPlanTitleBlock}>
                      <Text style={styles.aiEyebrow}>
                        {canAutoFix ? 'Can fix now' : 'Needs details'} ·{' '}
                        {Math.round((plan.confidence || 0) * 100)}%
                      </Text>
                      <Text style={styles.aiSummary}>{friendlyPlan.title}</Text>
                    </View>
                    <Badge
                      label={canAutoFix ? 'Ready to run' : 'Needs info'}
                      tone={canAutoFix ? 'green' : 'amber'}
                    />
                  </View>

                  <View style={styles.aiSection}>
                    <Text style={styles.aiSectionTitle}>What happened</Text>
                    <Text style={styles.aiText}>{friendlyPlan.why}</Text>
                  </View>

                  <View style={styles.aiSection}>
                    <Text style={styles.aiSectionTitle}>
                      What this fix will do
                    </Text>
                    {fixSteps.map((step, index) => (
                      <View key={`${step}-${index}`} style={styles.stepRow}>
                        <Text style={styles.stepNumber}>{index + 1}</Text>
                        <Text style={styles.stepText}>{step}</Text>
                      </View>
                    ))}
                  </View>

                  <View style={styles.aiSection}>
                    <Text style={styles.aiSectionTitle}>
                      After fixing, check
                    </Text>
                    <Text style={styles.aiText}>{friendlyPlan.check}</Text>
                    <Text style={styles.aiMeta}>
                      Rollback:{' '}
                      {plan.rollback?.description || 'Manual review required.'}
                    </Text>
                  </View>

                  {plan.backend_action ? (
                    <View style={styles.aiSection}>
                      <Text style={styles.aiSectionTitle}>
                        Technical payload
                      </Text>
                      <Text style={styles.payloadText}>
                        {JSON.stringify(
                          plan.backend_action.support_requests ||
                            plan.backend_action.fields ||
                            plan.backend_action,
                          null,
                          2,
                        )}
                      </Text>
                    </View>
                  ) : null}
                </>
              ) : (
                <Text style={styles.aiSummary}>
                  Agent diagnosis could not be loaded.
                </Text>
              )}

              {resolveResult ? (
                <View
                  style={[
                    styles.resolveResult,
                    resolveResult.success
                      ? styles.resolveResultSuccess
                      : styles.resolveResultFailed,
                  ]}>
                  <Text style={styles.resolveResultTitle}>
                    {resolveResult.proposed_rule
                      ? 'Rule saved'
                      : resolveResult.success
                      ? 'Fix completed'
                      : resolveResult.requires_input
                      ? 'Needs more information'
                      : 'Failed'}
                  </Text>
                  <Text style={styles.mini}>
                    {resolveResult.message || resolveResult.what_to_do || ''}
                  </Text>
                  {resolveResult.required_fields?.length ? (
                    <Text style={styles.mini}>
                      Required: {resolveResult.required_fields.join(', ')}
                    </Text>
                  ) : null}
                  {resolveResult.proposed_rule ? (
                    <Text style={styles.mini}>
                      Rule: {JSON.stringify(resolveResult.proposed_rule)}
                    </Text>
                  ) : null}
                </View>
              ) : null}
            </View>

            <View style={styles.compactDetails}>
              <View style={styles.compactDetailBlock}>
                <Text style={styles.resolutionBlockTitle}>
                  Why we flagged it
                </Text>
                <Text style={styles.reasonText}>
                  {evidenceSummary(primaryReason)}
                </Text>
                <Text style={styles.technicalDetails}>
                  Technical:{' '}
                  {primaryReason
                    ? `${stageLabel(primaryReason.stage)} / ${JSON.stringify(
                        primaryReason.evidence || {},
                      )}`
                    : 'No evidence returned.'}
                </Text>
              </View>
              <View style={styles.compactDetailBlock}>
                <Text style={styles.resolutionBlockTitle}>
                  Teach the helper
                </Text>
                <Text style={styles.mini}>
                  Use this when future answers need better wording, extra
                  checks, or a clearer manual fix.
                </Text>
                <View style={styles.teachSteps}>
                  <Text style={styles.teachStep}>
                    1. Say who it applies to.
                  </Text>
                  <Text style={styles.teachStep}>2. Say what to check.</Text>
                  <Text style={styles.teachStep}>3. Review before saving.</Text>
                </View>
                <Text style={styles.exampleText}>
                  Example: If classroom shows Registration Meeting, check Zoom
                  registration before changing class rows.
                </Text>
                <TextInput
                  multiline
                  value={contextText}
                  onChangeText={setContextText}
                  placeholder="Example: For this issue, first check whether..."
                  style={styles.contextInputCompact}
                />
                <TouchableOpacity
                  style={[
                    styles.manualResolveButton,
                    (!contextText.trim() || contextSaving || learningBusy) &&
                      styles.manualResolveDisabled,
                  ]}
                  disabled={
                    !contextText.trim() || contextSaving || learningBusy
                  }
                  onPress={() =>
                    openLearningReview('ops_context', contextText)
                  }>
                  <Text style={styles.manualResolveButtonText}>
                    {contextSaving || learningBusy
                      ? 'Preparing...'
                      : 'Review note'}
                  </Text>
                </TouchableOpacity>
              </View>
              <View style={styles.compactDetailBlock}>
                <Text style={styles.resolutionBlockTitle}>
                  Mark as wrong flag
                </Text>
                <Text style={styles.mini}>
                  Use this for false alarms. The helper will suggest a rule and
                  show it before saving.
                </Text>
                <Text style={styles.exampleText}>
                  Example: For all -MP- class names, mentor is not required.
                </Text>
                <TextInput
                  multiline
                  value={flagText}
                  onChangeText={setFlagText}
                  placeholder="Example: Students in this class type do not need mentors."
                  style={styles.contextInputCompact}
                />
                <TouchableOpacity
                  style={[
                    styles.manualResolveButton,
                    (!flagText.trim() || contextSaving || learningBusy) &&
                      styles.manualResolveDisabled,
                  ]}
                  disabled={!flagText.trim() || contextSaving || learningBusy}
                  onPress={() =>
                    openLearningReview('flag_suppression', flagText)
                  }>
                  <Text style={styles.manualResolveButtonText}>
                    {contextSaving || learningBusy
                      ? 'Checking...'
                      : 'Review rule'}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          </ScrollView>

          <View style={styles.modalActions}>
            <TouchableOpacity style={styles.closeButton} onPress={onClose}>
              <Text style={styles.closeButtonText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                canAutoFix
                  ? styles.autoResolveButton
                  : styles.manualResolveButton,
                resolving && styles.autoResolveDisabled,
              ]}
              disabled={resolving || agentLoading}
              onPress={onConfirm}>
              <Text
                style={
                  canAutoFix
                    ? styles.autoResolveButtonText
                    : styles.manualResolveButtonText
                }>
                {resolving
                  ? 'Working...'
                  : canAutoFix
                  ? 'Approve and run fix'
                  : 'Show what is missing'}
              </Text>
            </TouchableOpacity>
            {rollbackAuditId && resolveResult?.rollback?.available ? (
              <TouchableOpacity
                style={styles.rollbackButton}
                disabled={resolving}
                onPress={() => onRollback(rollbackAuditId)}>
                <Text style={styles.rollbackButtonText}>Rollback this fix</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        </View>
      </View>
      {learningDraft ? (
        <LearningConfirmModal
          draft={learningDraft}
          preview={learningPreview}
          busy={contextSaving || learningBusy}
          error={learningError}
          onCancel={() => {
            setLearningDraft(null);
            setLearningPreview(null);
            setLearningError('');
          }}
          onConfirm={confirmLearningSave}
        />
      ) : null}
    </Modal>
  );
};

const ruleSummary = (rule?: ResolveResult['proposed_rule']) => {
  if (!rule) {
    return 'This note will be added to the CSOC helper knowledge.';
  }
  const condition = rule.condition || {};
  if (condition.table && condition.field && condition.value) {
    return `${condition.table}.${condition.field} ${condition.operator} "${condition.value}"`;
  }
  return 'The helper could not make a narrow rule. Save only if this should stay as manual context.';
};

const LearningConfirmModal = ({
  draft,
  preview,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  draft: {contextType: LearningContextType; content: string};
  preview: ResolveResult | null;
  busy: boolean;
  error: string;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  const isFlagRule = draft.contextType === 'flag_suppression';
  const canConfirm = Boolean(preview?.success) && !busy;
  return (
    <Modal transparent animationType="fade" visible onRequestClose={onCancel}>
      <View style={styles.confirmScrim}>
        <View style={styles.learningModal}>
          <View style={styles.learningHeader}>
            <View style={styles.modalTitleBlock}>
              <Text style={styles.resolutionPanelTitle}>
                Review before saving
              </Text>
              <Text style={styles.muted}>
                Nothing is written to CSOC knowledge until you confirm.
              </Text>
            </View>
          </View>

          <ScrollView
            style={[styles.learningBody, styles.alwaysVerticalScroll]}
            showsVerticalScrollIndicator
            persistentScrollbar>
            <View style={styles.learningSection}>
              <Text style={styles.aiSectionTitle}>
                {isFlagRule ? 'Wrong-flag reason' : 'Teaching note'}
              </Text>
              <Text style={styles.learningQuote}>{draft.content}</Text>
            </View>

            <View style={styles.learningSection}>
              <Text style={styles.aiSectionTitle}>
                {isFlagRule ? 'Suggested rule' : 'What will be saved'}
              </Text>
              {busy && !preview ? (
                <Text style={styles.aiText}>Preparing preview...</Text>
              ) : (
                <>
                  <Text style={styles.aiText}>
                    {preview?.message ||
                      'This note will improve future CSOC helper answers.'}
                  </Text>
                  <Text style={styles.rulePreviewText}>
                    {ruleSummary(preview?.proposed_rule)}
                  </Text>
                </>
              )}
            </View>

            <View style={styles.learningSection}>
              <Text style={styles.aiSectionTitle}>After approval</Text>
              <Text style={styles.aiText}>
                {isFlagRule
                  ? 'Future matching students can stop being flagged after the dashboard refreshes.'
                  : 'The helper can use this note when answering questions or explaining future fixes.'}
              </Text>
              <Text style={styles.aiMeta}>
                Saved in cc_csoc_knowledge_documents and searchable by the CSOC
                helper.
              </Text>
            </View>

            {error ? <Text style={styles.error}>{error}</Text> : null}
          </ScrollView>

          <View style={styles.modalActions}>
            <TouchableOpacity style={styles.closeButton} onPress={onCancel}>
              <Text style={styles.closeButtonText}>Back to edit</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.autoResolveButton,
                !canConfirm && styles.autoResolveDisabled,
              ]}
              disabled={!canConfirm}
              onPress={onConfirm}>
              <Text style={styles.autoResolveButtonText}>
                {busy ? 'Saving...' : 'Confirm and save'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const UserRow = ({
  row,
  isCompact,
  onFixPress,
}: {
  row: HealthUser;
  isCompact: boolean;
  onFixPress: () => void;
}) => {
  const severity = row.health.severity;
  const stuckReasons = row.health.stuck_reasons || [];
  const primaryReason = stuckReasons[0];
  const primaryStage = row.health.primary_stage;
  const stageName = stageLabel(primaryStage);
  const problem = simplifyReasonMessage(primaryReason);
  const reasonEvidence = evidenceSummary(primaryReason);
  const actions = row.suggested_actions?.length
    ? row.suggested_actions.slice(0, 3)
    : ['No intervention required. Re-check if a new issue appears.'];
  const resolutions = stuckReasons.length
    ? stuckReasons
        .slice(0, 3)
        .map(reason => simplifyResolution(reason.resolution))
    : actions.map(action => simplifyResolution(action));
  return (
    <View style={styles.userRow}>
      <View style={styles.userCell}>
        <CellLabel>Student</CellLabel>
        <Text style={styles.userName}>{row.user.name || 'Unknown'}</Text>
        <Text style={styles.mini}>{row.user.mobile_number}</Text>
      </View>
      <View style={styles.orderCell}>
        <CellLabel>Order</CellLabel>
        <Text style={styles.orderText}>{row.order.order_id}</Text>
        <Text style={styles.mini}>
          {row.order.status}, {row.order.duration_in_months || 0} months
        </Text>
      </View>
      <View style={styles.stageCell}>
        <CellLabel>Blocked at</CellLabel>
        <Badge
          label={stageName}
          tone={
            severity === 'broken'
              ? 'red'
              : severity === 'warning'
              ? 'amber'
              : 'green'
          }
        />
        <Text style={styles.mini}>{severityLabel(severity)}</Text>
      </View>
      <View style={styles.issueCell}>
        <CellLabel>Latest note</CellLabel>
        <Text style={styles.issueText} numberOfLines={3}>
          {row.support.latest_issue_summary ||
            row.support.latest_request_type ||
            'No support note'}
        </Text>
        <Text style={styles.mini}>
          Issues: {row.support.open_issue_count || 0}, requests:{' '}
          {row.support.pending_request_count || 0}, outbox failed:{' '}
          {row.support.failed_outbox_count || 0}
        </Text>
      </View>
      <View style={styles.whyCell}>
        <CellLabel>Problem</CellLabel>
        <View style={styles.whyBox}>
          <Text style={styles.problemTitle}>{problem}</Text>
          <Text style={styles.reasonText}>{reasonEvidence}</Text>
          <Text style={styles.technicalHint}>
            Technical: {stageName} / {primaryStage || 'unknown'}
          </Text>
        </View>
      </View>
      <View style={[styles.resolutionCell, isCompact && styles.fullWidthCell]}>
        <View style={styles.resolutionHeader}>
          <Text style={styles.resolutionBlockTitle}>Next step</Text>
          <TouchableOpacity
            style={styles.resolutionButton}
            onPress={onFixPress}>
            <Text style={styles.resolutionButtonText}>Review fix</Text>
          </TouchableOpacity>
        </View>
        {resolutions.map((action, index) => (
          <Text key={`${action}-${index}`} style={styles.resolutionText}>
            {action}
          </Text>
        ))}
        {stuckReasons.length ? (
          <Text style={styles.technicalDetails}>
            Technical evidence: {JSON.stringify(primaryReason?.evidence || {})}
          </Text>
        ) : null}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  page: {flex: 1, backgroundColor: '#eef3f8'},
  alwaysVerticalScroll: {
    overflowY: 'scroll',
  },
  content: {padding: 24, gap: 16},
  contentNarrow: {padding: 12},
  topbar: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
  },
  topbarCompact: {alignItems: 'stretch'},
  title: {fontSize: 31, fontWeight: '900', color: '#111827'},
  muted: {color: '#5c6678'},
  scopeGroup: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    borderWidth: 1,
    borderColor: '#cfd8e3',
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#fff',
  },
  scopeGroupNarrow: {alignSelf: 'stretch'},
  scopeButton: {paddingVertical: 12, paddingHorizontal: 18},
  scopeActive: {backgroundColor: '#111827'},
  scopeText: {fontWeight: '800', color: '#5c6678'},
  scopeTextActive: {color: '#fff'},
  toolbar: {flexDirection: 'row', flexWrap: 'wrap', gap: 10},
  field: {
    minHeight: 44,
    borderWidth: 1,
    borderColor: '#cfd8e3',
    borderRadius: 8,
    paddingHorizontal: 13,
    justifyContent: 'center',
    backgroundColor: '#fff',
  },
  search: {minWidth: 260, flex: 1},
  searchNarrow: {minWidth: '100%'},
  filterActive: {backgroundColor: '#e8f6f4', borderColor: '#9fcfca'},
  filterText: {fontWeight: '800', color: '#111827'},
  error: {
    color: '#ba2424',
    backgroundColor: '#fff3f3',
    borderColor: '#efb3b3',
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
  },
  askPanel: {
    borderWidth: 1,
    borderColor: '#bfd4dc',
    borderRadius: 8,
    backgroundColor: '#f7fbfb',
    padding: 16,
    gap: 12,
  },
  askHeader: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: 12,
  },
  askTitleBlock: {flex: 1, minWidth: 260},
  askControls: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    alignItems: 'stretch',
  },
  askInput: {minWidth: 280, flex: 1},
  askButton: {
    minHeight: 44,
    borderRadius: 8,
    paddingHorizontal: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#0f766e',
  },
  askButtonDisabled: {backgroundColor: '#8ab7b2'},
  askButtonText: {color: '#fff', fontWeight: '900'},
  teachHint: {
    borderWidth: 1,
    borderColor: '#cfe1df',
    borderRadius: 8,
    backgroundColor: '#fff',
    padding: 10,
  },
  teachHintTitle: {
    color: '#0f766e',
    fontWeight: '900',
    marginBottom: 2,
  },
  answerBlock: {
    borderWidth: 1,
    borderColor: '#bdd8d4',
    borderRadius: 8,
    backgroundColor: '#fff',
    padding: 12,
  },
  answerText: {color: '#111827', lineHeight: 21, fontWeight: '700'},
  answerSources: {
    marginTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#d7e6e4',
    paddingTop: 10,
  },
  metrics: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  metric: {
    flexBasis: 210,
    flexGrow: 1,
    minHeight: 104,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#cfd8e3',
    borderLeftWidth: 5,
    backgroundColor: '#fff',
    padding: 15,
  },
  metricTitle: {fontSize: 13, color: '#5c6678'},
  metricValue: {fontSize: 34, lineHeight: 40, fontWeight: '900'},
  panel: {
    borderWidth: 1,
    borderColor: '#cfd8e3',
    borderRadius: 8,
    backgroundColor: '#fff',
    overflow: 'hidden',
  },
  panelHeader: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#cfd8e3',
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  panelTitle: {fontSize: 18, fontWeight: '900', color: '#111827'},
  cacheBadge: {
    backgroundColor: '#edf4ff',
    color: '#255fb3',
    borderRadius: 999,
    paddingVertical: 5,
    paddingHorizontal: 10,
    fontWeight: '900',
    alignSelf: 'flex-start',
  },
  flow: {
    padding: 22,
    gap: 22,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'stretch',
  },
  stageCard: {
    flexBasis: 190,
    flexGrow: 1,
    maxWidth: 260,
    minHeight: 190,
    borderWidth: 1,
    borderColor: '#cfd8e3',
    borderRadius: 8,
    backgroundColor: '#fbfcfe',
    padding: 13,
    paddingTop: 30,
  },
  stageSelected: {borderColor: '#255fb3', borderWidth: 2},
  stageNumber: {
    position: 'absolute',
    top: -18,
    left: 14,
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stageNumberText: {color: '#fff', fontWeight: '900'},
  stageTitle: {fontSize: 16, fontWeight: '900', color: '#111827'},
  stageSource: {fontSize: 12, color: '#5c6678', marginTop: 6},
  badgeRow: {flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 12},
  badge: {
    borderRadius: 999,
    paddingVertical: 4,
    paddingHorizontal: 8,
    fontSize: 12,
    fontWeight: '900',
    overflow: 'hidden',
  },
  stageDetails: {fontSize: 12, color: '#5c6678', marginTop: 12, lineHeight: 17},
  technicalHint: {
    fontSize: 11,
    color: '#7b8495',
    marginTop: 9,
    lineHeight: 15,
  },
  table: {},
  userRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    borderBottomWidth: 1,
    borderBottomColor: '#cfd8e3',
    padding: 14,
    gap: 14,
  },
  userCell: {flexBasis: 160, flexGrow: 1, minWidth: 0},
  orderCell: {flexBasis: 145, flexGrow: 1, minWidth: 0},
  stageCell: {flexBasis: 165, flexGrow: 1, minWidth: 0},
  issueCell: {flexBasis: 260, flexGrow: 2, minWidth: 0},
  whyCell: {flexBasis: 310, flexGrow: 2, minWidth: 0},
  resolutionCell: {flexBasis: 280, flexGrow: 2, minWidth: 0},
  fullWidthCell: {flexBasis: '100%'},
  userName: {fontSize: 16, fontWeight: '900', color: '#111827'},
  orderText: {fontWeight: '800', color: '#111827'},
  issueText: {color: '#111827', lineHeight: 20},
  cellLabel: {
    fontSize: 11,
    color: '#6b7280',
    fontWeight: '900',
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  mini: {fontSize: 13, color: '#5c6678', marginTop: 3},
  whyBox: {
    borderLeftWidth: 4,
    borderLeftColor: '#ba2424',
    backgroundColor: '#fff3f3',
    padding: 8,
    marginBottom: 6,
  },
  problemTitle: {
    fontSize: 15,
    fontWeight: '900',
    color: '#111827',
    lineHeight: 20,
  },
  reasonText: {color: '#334155', lineHeight: 20, marginTop: 5},
  technicalDetails: {
    color: '#6b7280',
    fontSize: 12,
    lineHeight: 17,
    marginTop: 8,
  },
  resolutionButton: {
    alignSelf: 'flex-start',
    backgroundColor: '#11724e',
    borderRadius: 7,
    paddingVertical: 9,
    paddingHorizontal: 16,
  },
  resolutionButtonText: {color: '#fff', fontWeight: '900'},
  resolutionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 10,
    marginBottom: 8,
  },
  resolutionText: {
    borderLeftWidth: 4,
    borderLeftColor: '#9e5f00',
    backgroundColor: '#fff6db',
    padding: 8,
    marginBottom: 6,
    lineHeight: 19,
  },
  modalScrim: {
    flex: 1,
    backgroundColor: 'rgba(17, 24, 39, 0.48)',
    padding: 18,
    justifyContent: 'center',
  },
  fixModal: {
    maxHeight: '92%',
    width: '100%',
    maxWidth: 1120,
    alignSelf: 'center',
    borderWidth: 1,
    borderColor: '#cfd8e3',
    borderRadius: 8,
    backgroundColor: '#fbfcfe',
    overflow: 'hidden',
  },
  modalBody: {
    maxHeight: 620,
  },
  resolutionPanelHeader: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: 12,
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#cfd8e3',
  },
  modalTitleBlock: {flex: 1, minWidth: 260},
  resolutionPanelTitle: {
    fontSize: 18,
    fontWeight: '900',
    color: '#111827',
  },
  closeButton: {
    alignSelf: 'flex-start',
    borderWidth: 1,
    borderColor: '#cfd8e3',
    borderRadius: 7,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#fff',
  },
  closeButtonText: {fontWeight: '900', color: '#111827'},
  autoResolveButton: {
    alignSelf: 'flex-start',
    backgroundColor: '#11724e',
    borderRadius: 7,
    paddingVertical: 10,
    paddingHorizontal: 14,
    marginTop: 4,
  },
  autoResolveDisabled: {backgroundColor: '#8aa99d'},
  autoResolveButtonText: {color: '#fff', fontWeight: '900'},
  manualResolveButton: {
    alignSelf: 'flex-start',
    borderWidth: 1,
    borderColor: '#cfd8e3',
    borderRadius: 7,
    paddingVertical: 10,
    paddingHorizontal: 14,
    marginTop: 4,
    backgroundColor: '#fff',
  },
  manualResolveDisabled: {backgroundColor: '#edf0f4'},
  manualResolveButtonText: {color: '#111827', fontWeight: '900'},
  rollbackButton: {
    alignSelf: 'flex-start',
    backgroundColor: '#fff3f3',
    borderWidth: 1,
    borderColor: '#efb3b3',
    borderRadius: 7,
    paddingVertical: 10,
    paddingHorizontal: 14,
    marginTop: 4,
  },
  rollbackButtonText: {color: '#ba2424', fontWeight: '900'},
  aiPlan: {
    margin: 16,
    borderWidth: 1,
    borderColor: '#bfd4dc',
    borderRadius: 8,
    backgroundColor: '#fff',
    padding: 16,
    gap: 14,
  },
  aiPlanHeader: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
  },
  aiPlanTitleBlock: {flex: 1, minWidth: 260},
  aiEyebrow: {
    color: '#0f766e',
    fontSize: 12,
    fontWeight: '900',
    textTransform: 'uppercase',
    marginBottom: 5,
  },
  aiSummary: {
    color: '#111827',
    fontSize: 20,
    lineHeight: 26,
    fontWeight: '900',
  },
  aiSection: {
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    paddingTop: 12,
  },
  aiSectionTitle: {
    color: '#6b7280',
    fontSize: 11,
    fontWeight: '900',
    textTransform: 'uppercase',
    marginBottom: 7,
  },
  aiText: {color: '#111827', lineHeight: 21, fontWeight: '700'},
  aiMeta: {color: '#5c6678', lineHeight: 20, marginTop: 8},
  payloadText: {
    color: '#111827',
    fontFamily: 'monospace',
    fontSize: 12,
    lineHeight: 17,
    backgroundColor: '#f4f7fb',
    borderWidth: 1,
    borderColor: '#d8e0ea',
    borderRadius: 8,
    padding: 10,
  },
  stepRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    marginBottom: 8,
  },
  stepNumber: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#0f766e',
    color: '#fff',
    textAlign: 'center',
    lineHeight: 24,
    fontWeight: '900',
    overflow: 'hidden',
  },
  stepText: {flex: 1, color: '#111827', lineHeight: 21, fontWeight: '700'},
  compactDetails: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  compactDetailBlock: {
    flex: 1,
    minWidth: 280,
    borderWidth: 1,
    borderColor: '#d8e0ea',
    borderRadius: 8,
    backgroundColor: '#fff',
    padding: 12,
  },
  agentSummaryGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 8,
  },
  agentPill: {
    borderWidth: 1,
    borderColor: '#cfd8e3',
    borderRadius: 8,
    backgroundColor: '#f7fafc',
    padding: 9,
    minWidth: 120,
  },
  agentPillLabel: {
    color: '#6b7280',
    fontSize: 11,
    fontWeight: '900',
    textTransform: 'uppercase',
  },
  agentPillValue: {
    color: '#111827',
    fontWeight: '900',
    marginTop: 3,
  },
  knowledgeBox: {
    borderLeftWidth: 4,
    borderLeftColor: '#255fb3',
    backgroundColor: '#edf4ff',
    padding: 8,
    marginBottom: 8,
  },
  contextInput: {
    minHeight: 118,
    borderWidth: 1,
    borderColor: '#cfd8e3',
    borderRadius: 8,
    backgroundColor: '#fff',
    color: '#111827',
    padding: 10,
    marginTop: 10,
    marginBottom: 8,
    textAlignVertical: 'top',
  },
  contextInputCompact: {
    minHeight: 76,
    borderWidth: 1,
    borderColor: '#cfd8e3',
    borderRadius: 8,
    backgroundColor: '#fff',
    color: '#111827',
    padding: 10,
    marginBottom: 8,
    textAlignVertical: 'top',
  },
  teachSteps: {
    borderLeftWidth: 4,
    borderLeftColor: '#0f766e',
    backgroundColor: '#f0fbf9',
    padding: 8,
    marginTop: 8,
    marginBottom: 8,
  },
  teachStep: {
    color: '#334155',
    fontSize: 13,
    lineHeight: 19,
    fontWeight: '700',
  },
  exampleText: {
    color: '#5c6678',
    fontSize: 13,
    lineHeight: 18,
    marginBottom: 8,
    fontStyle: 'italic',
  },
  resolveResult: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 10,
    marginTop: 10,
  },
  resolveResultSuccess: {
    backgroundColor: '#e9f8f1',
    borderColor: '#a8d8c2',
  },
  resolveResultFailed: {
    backgroundColor: '#fff3f3',
    borderColor: '#efb3b3',
  },
  resolveResultTitle: {fontWeight: '900', color: '#111827'},
  resolutionColumns: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    padding: 16,
  },
  resolutionBlock: {
    flex: 1,
    minWidth: 300,
    borderWidth: 1,
    borderColor: '#cfd8e3',
    borderRadius: 8,
    backgroundColor: '#fff',
    padding: 12,
  },
  resolutionBlockTitle: {
    fontWeight: '900',
    color: '#111827',
    marginBottom: 8,
  },
  modalActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    gap: 10,
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#cfd8e3',
    backgroundColor: '#fff',
  },
  confirmScrim: {
    flex: 1,
    backgroundColor: 'rgba(17, 24, 39, 0.62)',
    padding: 18,
    justifyContent: 'center',
  },
  learningModal: {
    width: '100%',
    maxWidth: 720,
    alignSelf: 'center',
    borderWidth: 1,
    borderColor: '#cfd8e3',
    borderRadius: 8,
    backgroundColor: '#fbfcfe',
    overflow: 'hidden',
  },
  learningHeader: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#cfd8e3',
    backgroundColor: '#fff',
  },
  learningBody: {
    padding: 16,
    gap: 12,
  },
  learningSection: {
    borderWidth: 1,
    borderColor: '#d8e0ea',
    borderRadius: 8,
    backgroundColor: '#fff',
    padding: 12,
  },
  learningQuote: {
    color: '#111827',
    lineHeight: 21,
    fontWeight: '700',
  },
  rulePreviewText: {
    color: '#111827',
    fontFamily: 'monospace',
    fontSize: 12,
    lineHeight: 17,
    backgroundColor: '#f4f7fb',
    borderWidth: 1,
    borderColor: '#d8e0ea',
    borderRadius: 8,
    padding: 10,
    marginTop: 10,
  },
  denied: {padding: 28},
  deniedTitle: {fontSize: 24, fontWeight: '900', color: '#111827'},
  redBorder: {borderLeftColor: '#ba2424'},
  amberBorder: {borderLeftColor: '#9e5f00'},
  greenBorder: {borderLeftColor: '#11724e'},
  redText: {color: '#ba2424'},
  amberText: {color: '#9e5f00'},
  greenText: {color: '#11724e'},
  redBg: {backgroundColor: '#ba2424'},
  amberBg: {backgroundColor: '#9e5f00'},
  greenBg: {backgroundColor: '#11724e'},
  redSoft: {backgroundColor: '#fff3f3'},
  amberSoft: {backgroundColor: '#fff6db'},
  greenSoft: {backgroundColor: '#e9f8f1'},
});

export default CSOCHealthDashboard;
